import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import type { FC, ReactNode } from 'react';

import { useSession } from '@hooks/useSession';

import type { ChatSettings } from '@models/chat';

import {
  useGetChatRoomLazyQuery,
  useInitiateMessengerAuthMutation,
} from 'generated/chat.generated';

interface MessengerState {
  isInitialized: boolean;
  renderChat: boolean;
  token: string;
  chatRoomId: string;
  settings: ChatSettings;
}

export enum MessengerActions {
  // renderChat: true -> getChatSession -> update state with token, room ID, settings -> isInitialized: false
  InitializeEnabledChat = 'initializeEnabledChat',
  // renderChat: false -> isInitialized: false
  InitializeDisabledChat = 'initializeDisabledChat',
  Reset = 'reset',
}

type MessengerPayload = {
  [MessengerActions.InitializeEnabledChat]: {
    token: string;
    chatRoomId: string;
    renderChat: boolean;
    settings: ChatSettings;
  };
  [MessengerActions.InitializeDisabledChat]: undefined;
  [MessengerActions.Reset]: undefined;
};

interface MessengerContext {
  state: MessengerState;
  dispatch: React.Dispatch<Action<MessengerPayload>>;
  handleReset: () => void;
}

const reducer = (
  state: MessengerState,
  action: Action<MessengerPayload>
): MessengerState => {
  switch (action.type) {
    case MessengerActions.InitializeEnabledChat:
      return {
        ...state,
        chatRoomId: action.chatRoomId,
        token: action.token,
        settings: action.settings,
        renderChat: action.renderChat,
        isInitialized: true,
      };
    case MessengerActions.InitializeDisabledChat:
      return {
        ...state,
        isInitialized: true,
      };
    case MessengerActions.Reset:
      // Return to the fully uninitialized state so the next authenticated
      // user re-runs fetchChatSession (the init effect is guarded on
      // !state.isInitialized) and Ethora is never mounted with a stale/empty
      // token. MessengerProvider stays mounted across logout -> login, so
      // leaving isInitialized/renderChat truthy here would leak the previous
      // user's chat session.
      return initialMessengerState;
    default:
      return state;
  }
};

const initialMessengerState: MessengerState = {
  isInitialized: false,
  renderChat: false,
  token: '',
  chatRoomId: '',
  settings: { baseUrl: '', conference: '', devServer: '', host: '' },
};

export const MessengerContext = createContext<MessengerContext>({
  state: initialMessengerState,
  dispatch: () => null,
  handleReset: () => null,
});

MessengerContext.displayName = 'MessengerContext';

export const MessengerProvider: FC<{ children: ReactNode }> = ({
  children = null,
}) => {
  const [state, dispatch] = useReducer(reducer, initialMessengerState);
  // Tracks which logged-in user the chat session was initialized for, so we
  // re-run initiateMessengerAuth whenever the user changes (login, logout ->
  // login, profile switch) instead of relying on a sticky isInitialized flag
  // that could leave the previous user's session in place.
  const initializedUserIdRef = useRef<string | null>(null);

  const {
    state: {
      active_user_id,
      isAuthenticated,
      isLoadingAuth,
      isOrg,
      isPatient,
      logged_in_user_id,
    },
  } = useSession();

  const isMessengerEnabled = isOrg || isPatient;

  const [logIn] = useInitiateMessengerAuthMutation();
  const [getChatRoom] = useGetChatRoomLazyQuery();

  /**
   * Get the logged in user's chat session (token and chat room ID).
   *
   * `requestedUserId` is the logged-in user this fetch was started for. Because
   * the auth/room requests are async and un-cancellable, a fast profile switch
   * can leave an earlier request in flight while a newer one starts. We compare
   * against initializedUserIdRef after every await and drop the result if it no
   * longer matches, so one user's chat token can never be mounted for another.
   */
  const fetchChatSession = useCallback(
    async (requestedUserId: string | null) => {
      try {
        let chat_room_id = '';
        const response = await logIn();

        if (initializedUserIdRef.current !== requestedUserId) {
          return;
        }

        if (isPatient) {
          const { data: chatRoomData } = await getChatRoom({
            variables: {
              input: {
                fhir_patient_id: null,
              },
            },
          });
          chat_room_id = chatRoomData?.getChatRoom.name ?? '';
        }

        if (response.data?.initiateMessengerAuth) {
          const { config, token } = response.data.initiateMessengerAuth;
          dispatch({
            type: MessengerActions.InitializeEnabledChat,
            chatRoomId: chat_room_id,
            token,
            settings: {
              baseUrl: config.base_url,
              conference: config.conference,
              devServer: config.dev_server,
              host: config.host,
            },
            renderChat: true,
          });
        } else {
          dispatch({
            type: MessengerActions.InitializeDisabledChat,
          });
        }
      } catch {
        // A chat auth/room request failed (e.g. a transient network error).
        // Only act if this request is still the current one, so a stale failure
        // can't clobber a newer user's session. Clear the init marker so the
        // effect re-runs fetchChatSession for this user on a later render
        // instead of leaving the messenger permanently uninitialized, and
        // disable the chat in the meantime.
        if (initializedUserIdRef.current !== requestedUserId) {
          return;
        }
        initializedUserIdRef.current = null;
        dispatch({ type: MessengerActions.InitializeDisabledChat });
      }
    },
    [getChatRoom, isPatient, logIn]
  );

  const handleReset = useCallback(() => {
    initializedUserIdRef.current = null;
    dispatch({ type: MessengerActions.Reset });
  }, []);

  useEffect(() => {
    // No authenticated user yet (or a logout is in progress): drop the init
    // marker so the next user to sign in re-runs initiateMessengerAuth.
    if (isLoadingAuth || !isAuthenticated) {
      initializedUserIdRef.current = null;
      return;
    }

    /**
     * Check if the chat should be shown - appears when the messenger is enabled
     * for the environment, the user is an org member or patient, and the logged
     * in and active user are the same (curation view will not display chat).
     */
    const renderMessenger =
      isMessengerEnabled && Boolean(logged_in_user_id === active_user_id);

    if (!renderMessenger) {
      dispatch({ type: MessengerActions.InitializeDisabledChat });
      return;
    }

    // Only (re)initialize when the logged-in user actually changes. This runs
    // initiateMessengerAuth for every distinct user, so switching practitioners
    // always fetches a fresh chat token instead of reusing the prior session.
    if (initializedUserIdRef.current !== logged_in_user_id) {
      initializedUserIdRef.current = logged_in_user_id;
      fetchChatSession(logged_in_user_id);
    }
  }, [
    active_user_id,
    fetchChatSession,
    isAuthenticated,
    isLoadingAuth,
    isMessengerEnabled,
    isOrg,
    isPatient,
    logged_in_user_id,
  ]);

  const value: MessengerContext = useMemo(() => {
    return {
      state,
      dispatch,
      handleReset,
    };
  }, [handleReset, state]);

  return (
    <MessengerContext.Provider value={value}>
      {children}
    </MessengerContext.Provider>
  );
};
